import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Cpu, Database, Brain, Lock, Server, Cloud, Sparkles } from 'lucide-react';

const TechStackSection = () => {
  // IBM Logo - Bold text style (clean and professional)
  const IBMLogo = () => (
    <span 
      className="text-2xl font-black tracking-tight text-white"
      style={{ fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '-0.05em' }}
    >
      IBM
    </span>
  );

  // AWS Logo - Bold text with orange smile
  const AWSLogo = () => (
    <div className="flex items-center gap-1">
      <span 
        className="text-2xl font-black tracking-tight text-white"
        style={{ fontFamily: 'Arial Black, Arial, sans-serif', letterSpacing: '-0.02em' }}
      >
        AWS
      </span>
      <svg viewBox="0 0 30 20" className="w-4 h-3 -ml-1 mt-2">
        <path d="M2 8 Q15 18 28 8" stroke="#FF9900" strokeWidth="4" fill="none" strokeLinecap="round"/>
      </svg>
    </div>
  );

  const technologies = [
    {
      name: 'Claude AI',
      description: 'Procesamiento de lenguaje natural avanzado',
      icon: Brain,
      category: 'IA'
    },
    {
      name: 'MiroFish',
      description: 'Motor de simulación predictiva propietario',
      icon: Sparkles,
      category: 'IA'
    },
    {
      name: 'Supabase',
      description: 'Base de datos en tiempo real segura',
      icon: Database,
      category: 'Data'
    },
    {
      name: 'TensorFlow',
      description: 'Machine learning de producción',
      icon: Cpu,
      category: 'IA'
    },
    {
      name: 'OpenAI GPT',
      description: 'Análisis de datos no estructurados',
      icon: Brain,
      category: 'IA'
    },
    {
      name: 'Apache Kafka',
      description: 'Streaming de datos empresarial',
      icon: Server,
      category: 'Data'
    },
    {
      name: 'Kubernetes',
      description: 'Orquestación y escalabilidad',
      icon: Cloud,
      category: 'Infra'
    },
    {
      name: 'Vault',
      description: 'Gestión de secretos y encriptación',
      icon: Lock,
      category: 'Security'
    },
  ];

  const partners = [
    {
      name: 'IBM',
      Logo: IBMLogo,
      description: 'Partner Tecnológico',
    },
    {
      name: 'AWS',
      Logo: AWSLogo,
      description: 'Partner Cloud',
    },
  ];

  return (
    <section id="tech-stack" className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
            data-cms="tech.title"
          >
            Tecnología de Clase Mundial
          </h2>
          <p 
            className="text-slate-400 max-w-2xl mx-auto"
            data-cms="tech.subtitle"
          >
            Utilizamos las herramientas más avanzadas de inteligencia artificial 
            y seguridad empresarial para garantizar resultados.
          </p>
        </motion.div>

        {/* Partners Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <span className="text-sm text-slate-500 uppercase tracking-wider">Partners Oficiales</span>
            <div className="flex items-center gap-8">
              {partners.map((partner, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-4 px-6 py-4 rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-blue-500/30 transition-all"
                >
                  <div className="text-white">
                    <partner.Logo />
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs block">{partner.description}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Technologies Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {technologies.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ 
                scale: 1.03,
                transition: { duration: 0.2 }
              }}
              className="group relative p-5 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04] hover:border-blue-500/20 transition-all duration-300"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-800 to-slate-700 flex items-center justify-center shrink-0 group-hover:from-blue-500/20 group-hover:to-cyan-500/20 transition-all duration-300">
                  <tech.icon className="w-5 h-5 text-slate-400 group-hover:text-blue-400 transition-colors" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-sm mb-1">
                    {tech.name}
                  </h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    {tech.description}
                  </p>
                </div>
              </div>
              
              {/* Category badge */}
              <div className="absolute top-3 right-3">
                <span className="text-[10px] text-slate-600 uppercase tracking-wider">
                  {tech.category}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Security Note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <Lock className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400">
              Infraestructura certificada SOC 2 · ISO 27001 · GDPR Compliant
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechStackSection;
